import { Mic, Pause, Play, Square } from 'lucide-react';
import { useOptionalRecording } from './RecordingContext';
import { formatDurationMS } from '@/utils/date';

interface Props {
  /** Callback opcional pra reabrir o modal Gravar (clicar na pílula). */
  onOpenRecorder?: () => void;
}

/**
 * Pílula flutuante visível durante toda a gravação. Renderiza só quando
 * isRecording=true e o Provider está montado. Permite pausar/retomar/parar
 * sem reabrir o modal e clicar na zona principal pra abrir o gravador.
 */
export const RecordingIndicatorPill = ({ onOpenRecorder }: Props) => {
  const ctx = useOptionalRecording();
  if (!ctx || !ctx.isRecording) return null;
  const { isPaused, recordingTime, pauseResume, stop } = ctx;

  return (
    <div
      className="fixed bottom-4 left-4 z-[60] flex items-center gap-2 pl-3 pr-2 py-2 rounded-full bg-surface shadow-2xl border border-error/40 ring-2 ring-error/20"
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={onOpenRecorder}
        className="flex items-center gap-2 group"
        title={onOpenRecorder ? 'Abrir gravador' : 'Gravando'}
      >
        <span className={`w-2.5 h-2.5 rounded-full bg-error ${!isPaused ? 'animate-pulse' : ''}`} />
        <span className="font-mono font-bold text-base tabular-nums text-on-surface group-hover:text-primary transition-colors">
          {formatDurationMS(recordingTime)}
        </span>
        {isPaused && <span className="text-xs text-on-surface-variant">Pausado</span>}
        {onOpenRecorder && (
          <Mic size={14} className="text-on-surface-variant group-hover:text-primary transition-colors" />
        )}
      </button>
      <div className="w-px h-5 bg-outline-variant/40 mx-0.5" />
      <button
        type="button"
        onClick={pauseResume}
        className="w-7 h-7 rounded-full hover:bg-surface-container flex items-center justify-center text-on-surface-variant transition-colors"
        title={isPaused ? 'Retomar gravação' : 'Pausar gravação'}
      >
        {isPaused ? <Play size={14} /> : <Pause size={14} />}
      </button>
      <button
        type="button"
        onClick={stop}
        className="w-7 h-7 rounded-full bg-error/10 hover:bg-error/20 flex items-center justify-center text-error transition-colors"
        title="Parar gravação"
      >
        <Square size={12} className="fill-current" />
      </button>
    </div>
  );
};
